import manifest from './facilities.json';
import type { Antenna } from '@/features/loader/types';

export interface FacilityRecord {
  id: string;
  name: string;
  operator: string;
  location: string;
  role: string;
  sourceTitle: string;
  sourceUrl: string;
  checkedAt: string;
  note: string;
  lat: number;
  lon: number;
  extentM: number;
  mapUrl?: string;
  members: { network: string; antennaId: string }[];
}
export interface SiteMapFeature {
  id: number | string;
  kind: string;
  tags: Record<string, string>;
  coordinates: [number, number][];
  holes?: [number, number][][];
  display?: boolean;
  roofSurface?: import('@/features/twin/site/roofSurface').RoofSurface;
  source?: {
    provider: string;
    url: string;
    license: string;
    retrievedAt: string;
    release?: string;
    upstream?: string[];
  };
}
export interface FacilityMap {
  license?: string;
  retrievedAt?: string;
  sourceUrl?: string;
  attribution?: string;
  overtureRelease?: string;
  coverage?: {
    osmBuildings: number;
    overtureBuildings: number;
    displayedBuildings: number;
    duplicatesRemoved: number;
    extentM: number;
    locationQuality: string;
    note: string;
  };
  features: SiteMapFeature[];
}
export const FACILITIES: readonly FacilityRecord[] = manifest.facilities;
const byId = new Map(FACILITIES.map((f) => [f.id, f]));
const byAntenna = new Map(
  FACILITIES.flatMap((f) => f.members.map((m) => [`${m.network}:${m.antennaId}`, f] as const)),
);
export function facilityById(id?: string): FacilityRecord | undefined {
  return id ? byId.get(id) : undefined;
}
export function facilityForAntenna(network: string, id: string): FacilityRecord | undefined {
  return byAntenna.get(`${network === 'noaa_extended' ? 'noaa' : network}:${id}`);
}
export const facilityAssetId = (id: string) => `facility:${id}`;
export const facilitiesForNetwork = (network: string) =>
  FACILITIES.filter((f) =>
    f.members.some((m) => m.network === (network === 'noaa_extended' ? 'noaa' : network)),
  );

export function antennaSite(antenna: Antenna, network: string) {
  const facility = facilityForAntenna(network, antenna.id);
  return {
    antennaId: antenna.id,
    lat: Number(antenna.location.lat),
    lon: Number(antenna.location.lon),
    facilityId: facility?.id,
    location: antenna.location.name,
    mapUrl: facility?.mapUrl,
    sourceUrl: facility?.sourceUrl,
    sourceTitle: facility?.sourceTitle,
    description: `Antenna location and diameter follow the ${network.toUpperCase()} planning catalogue. Mount, reflector type, enclosure and equipment layout are representative unless separately documented. No live telemetry is assigned.${facility ? ` ${facility.note}` : ''}`,
  };
}
